import React, { useState } from 'react';
import { Check, X, ListChecks, ClipboardCheck } from 'lucide-react';

const ManualGuidanceChecklist = ({ workflow, onComplete, onClose }) => {
    const [checkedSteps, setCheckedSteps] = useState({});

    // Guidance is expected to be an array of strings or objects in workflow.payload.steps
    const steps = Array.isArray(workflow.payload?.steps)
        ? workflow.payload.steps
        : (typeof workflow.payload === 'string' ? workflow.payload.split('\\n') : ['Please follow the provided guidance in the notes section.']);

    const toggleStep = (index) => {
        setCheckedSteps(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const allStepsCompleted = steps.every((_, index) => checkedSteps[index]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-6 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="p-8 pb-4 flex items-center justify-between border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-xl">
                            <ListChecks size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">Manual Guidance</h3>
                            <p className="text-slate-500 text-xs font-medium">Follow these steps to complete the task</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-full hover:bg-slate-100">
                        <X size={24} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-8 overflow-y-auto flex-1">
                    <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl">
                        <p className="text-emerald-700 text-sm font-medium leading-relaxed">
                            {workflow.notes || "Perform the following actions manually in the platform or external tools."}
                        </p>
                    </div>

                    <div className="space-y-3">
                        {steps.map((step, index) => (
                            <div
                                key={index}
                                onClick={() => toggleStep(index)}
                                className={`group flex items-start gap-4 p-4 rounded-2xl cursor-pointer transition-all border-2 ${
                                    checkedSteps[index]
                                    ? 'bg-emerald-50 border-emerald-200'
                                    : 'bg-white border-slate-100 hover:border-emerald-200 hover:bg-slate-50'
                                }`}
                            >
                                <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                    checkedSteps[index] ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 group-hover:border-emerald-400'
                                }`}>
                                    {checkedSteps[index] && <Check size={12} className="text-white" />}
                                </div>
                                <p className={`text-sm font-medium leading-relaxed transition-all ${
                                    checkedSteps[index] ? 'text-emerald-700 line-through opacity-60' : 'text-slate-600'
                                }`}>
                                    {typeof step === 'string' ? step : step.text || 'Unnamed Step'}
                                </p>
                            </div>
                        ))}
                    </div></div>

                {/* Footer */}
                <div className="p-8 pt-4 flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-slate-700 transition-colors"
                    >
                        Close
                    </button>
                    <button
                        disabled={!allStepsCompleted}
                        onClick={() => onComplete(workflow.notes)}
                        className={`px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg flex items-center gap-2 ${
                            allStepsCompleted
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                        }`}
                    >
                        <ClipboardCheck size={16} />
                        Mark as Complete
                    </button>
                </div></div></div>);
};

export default ManualGuidanceChecklist;
